import React from "react";
import { Draggable } from "@hello-pangea/dnd";
import Tooltip from "@mui/material/Tooltip";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ChecklistIcon from "@mui/icons-material/Checklist";

// Maps a BoardLabel color to its Tailwind chip background.
export const LABEL_COLOR_CLASSES = {
  green: "bg-emerald-500",
  yellow: "bg-yellow-400",
  orange: "bg-orange-500",
  red: "bg-red-500",
  purple: "bg-violet-500",
  blue: "bg-blue-500",
  sky: "bg-sky-400",
  lime: "bg-lime-500",
  pink: "bg-pink-500",
  slate: "bg-slate-500",
};

// Format a "YYYY-MM-DD" string into a short "May 18" label without any
// timezone shift (constructing from parts keeps it as a local date).
function formatDueDate(dueStr) {
  if (!dueStr) return "";
  const [y, m, d] = dueStr.split("-").map(Number);
  if (!y || !m || !d) return "";
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function todayStr() {
  const now = new Date();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${m}-${d}`;
}

const BoardCardTile = ({ card, index, onClick, onArchive }) => {
  const labels = card.labels || [];
  const members = card.members || [];
  const commentCount = card.comment_count || 0;
  const attachmentCount = (card.file_uploads || []).length;
  const hasDescription = !!(card.description && card.description.trim());

  let checklistTotal = 0;
  let checklistCompleted = 0;
  for (const cl of card.checklists || []) {
    const items = cl.items || [];
    checklistTotal += items.length;
    checklistCompleted += items.filter((i) => i.is_complete).length;
  }

  const hasFooterBadges =
    !!card.due_date ||
    checklistTotal > 0 ||
    attachmentCount > 0 ||
    commentCount > 0;

  // Due-date pill color: green when complete, red when overdue, slate otherwise.
  let duePillClasses = "bg-slate-100 text-slate-600";
  if (card.due_date) {
    if (card.due_complete) {
      duePillClasses = "bg-emerald-100 text-emerald-700";
    } else if (card.due_date < todayStr()) {
      duePillClasses = "bg-red-100 text-red-700";
    }
  }

  return (
    <Draggable draggableId={card.uuid} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={() => onClick && onClick(card)}
          className={`group relative bg-white rounded-lg border px-3 py-2 mb-2 cursor-pointer ${
            snapshot.isDragging
              ? "border-slate-300 shadow-lg"
              : "border-slate-200 hover:border-slate-300"
          }`}
        >
          {/* Archive affordance — revealed on hover */}
          <button
            className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              onArchive && onArchive(card);
            }}
          >
            <DeleteOutlineIcon style={{ fontSize: 16 }} />
          </button>

          {/* Label chips */}
          {labels.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-1.5">
              {labels.map((label) => (
                <Tooltip key={label.uuid} title={label.name || ""} arrow>
                  <div
                    className={`h-2 w-9 rounded-full ${
                      LABEL_COLOR_CLASSES[label.color] || "bg-slate-400"
                    }`}
                  />
                </Tooltip>
              ))}
            </div>
          )}

          {/* Title */}
          <p className="text-sm text-slate-800 pr-4 whitespace-pre-wrap break-words">
            {card.title}
          </p>

          {/* Description */}
          {hasDescription && (
            <p className="text-xs text-slate-500 mt-1 whitespace-pre-wrap break-words">
              {card.description}
            </p>
          )}

          {/* Footer badges */}
          {hasFooterBadges && (
            <div className="flex items-center gap-2 mt-2">
              {card.due_date && (
                <span
                  className={`flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded ${duePillClasses}`}
                >
                  <EventOutlinedIcon style={{ fontSize: 13 }} />
                  {formatDueDate(card.due_date)}
                </span>
              )}
              {checklistTotal > 0 && (
                <span
                  className={`flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded ${
                    checklistCompleted === checklistTotal
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  <ChecklistIcon style={{ fontSize: 13 }} />
                  {checklistCompleted}/{checklistTotal}
                </span>
              )}
              {attachmentCount > 0 && (
                <span className="flex items-center gap-0.5 text-xs text-slate-500">
                  <AttachFileIcon style={{ fontSize: 13 }} />
                  {attachmentCount}
                </span>
              )}
              {commentCount > 0 && (
                <span className="flex items-center gap-0.5 text-xs text-slate-500">
                  <ChatOutlinedIcon style={{ fontSize: 13 }} />
                  {commentCount}
                </span>
              )}
            </div>
          )}

          {/* Assignees — on their own row */}
          {members.length > 0 && (
            <div className="flex items-center -space-x-1 mt-2">
              {members.slice(0, 4).map((m) => (
                <Tooltip key={m.id} title={m.full_name || ""} arrow>
                  <div className="h-6 w-6 rounded-full bg-violet-100 border border-white flex items-center justify-center">
                    <span className="text-[11px] font-semibold text-violet-700">
                      {m.initials}
                    </span>
                  </div>
                </Tooltip>
              ))}
              {members.length > 4 && (
                <div className="h-6 w-6 rounded-full bg-slate-100 border border-white flex items-center justify-center">
                  <span className="text-[11px] font-semibold text-slate-600">
                    +{members.length - 4}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
};

export default BoardCardTile;
